export const TRANSLATION_LANGUAGES = [
    { code: 'en', name: 'English', nativeName: 'English', dir: 'ltr', script: 'latin' },
    { code: 'ar', name: 'Arabic', nativeName: 'العربية', dir: 'rtl', script: 'arabic' },
    { code: 'ru', name: 'Russian', nativeName: 'Русский', dir: 'ltr', script: 'cyrillic' },
    { code: 'tr', name: 'Turkish', nativeName: 'Türkçe', dir: 'ltr', script: 'latin' },
    { code: 'uk', name: 'Ukrainian', nativeName: 'Українська', dir: 'ltr', script: 'cyrillic' },
    { code: 'es', name: 'Spanish', nativeName: 'Español', dir: 'ltr', script: 'latin' },
    { code: 'fa', name: 'Persian', nativeName: 'فارسی', dir: 'rtl', script: 'arabic' },
    { code: 'pl', name: 'Polish', nativeName: 'Polski', dir: 'ltr', script: 'latin' },
    { code: 'ro', name: 'Romanian', nativeName: 'Română', dir: 'ltr', script: 'latin' },
];
export const DEFAULT_TRANSLATION_LANGUAGE = 'en';
/** Every language except English: the keys of `Translations`. */
export const EXTRA_TRANSLATION_LANGUAGES = TRANSLATION_LANGUAGES.filter((l) => l.code !== 'en').map((l) => l.code);
export function isTranslationLanguage(value) {
    return TRANSLATION_LANGUAGES.some((l) => l.code === value);
}
/** A stored or requested language code, or English when it isn't one of ours. */
export function normalizeTranslationLanguage(value) {
    return isTranslationLanguage(value) ? value : DEFAULT_TRANSLATION_LANGUAGE;
}
export function languageInfo(code) {
    return TRANSLATION_LANGUAGES.find((l) => l.code === code) ?? TRANSLATION_LANGUAGES[0];
}
// Bidi overrides/isolates and directional marks could reorder or spoof displayed text; ZWNJ/ZWJ
// (U+200C/U+200D) are kept because Persian spelling needs them (e.g. "می‌رود").
const UNSAFE_CHARS = 
// eslint-disable-next-line no-control-regex
/[\u0000-\u001f\u007f-\u009f\u200b\u200e\u200f\u202a-\u202e\u2060-\u2069\ufeff]/g;
function cleanText(value, max) {
    if (typeof value !== 'string')
        return null;
    const clean = value.replace(UNSAFE_CHARS, ' ').replace(/\s+/g, ' ').trim();
    return clean && clean.length <= max ? clean : null;
}
/** Persian is written with Persian yeh/kaf (ی ک); the AI sometimes returns the Arabic forms (ي ك). */
function normalizePersian(text) {
    return text.replace(/ي/g, 'ی').replace(/ك/g, 'ک');
}
const SCRIPT_RE = {
    latin: /\p{Script=Latin}/u,
    cyrillic: /\p{Script=Cyrillic}/u,
    arabic: /\p{Script=Arabic}/u,
};
/**
 * Whether `text` is plausibly written in `lang`: most letters in the language's script (a German
 * name such as "Hamburg" inside an Arabic sentence is fine), plus a few letters that tell close
 * languages apart (Russian vs Ukrainian, Arabic vs Persian).
 */
export function matchesLanguageScript(text, lang) {
    const letters = text.match(/\p{L}/gu);
    if (!letters?.length)
        return false;
    const info = languageInfo(lang);
    const inScript = letters.filter((ch) => SCRIPT_RE[info.script].test(ch)).length;
    if (inScript / letters.length < (info.script === 'latin' ? 0.8 : 0.5))
        return false;
    if (lang === 'uk' && /[ыэъЫЭЪ]/.test(text))
        return false;
    if (lang === 'ru' && /[іїєґІЇЄҐ]/.test(text))
        return false;
    if (lang === 'ar' && /[پچژگ]/.test(text))
        return false;
    return true;
}
function sanitizeOne(raw, lang) {
    if (!raw || typeof raw !== 'object')
        return null;
    const r = raw;
    let translation = cleanText(r.translation, 120);
    let example = cleanText(r.example, 300);
    if (lang === 'fa') {
        translation = translation && normalizePersian(translation);
        example = example && normalizePersian(example);
    }
    if (!translation || !matchesLanguageScript(translation, lang))
        return null;
    if (example && !matchesLanguageScript(example, lang))
        example = null;
    return example ? { translation, example } : { translation };
}
/**
 * Keeps only well-formed translations for our extra languages (from the server, a sync or a
 * bundled file); anything malformed or in the wrong script is dropped, never patched.
 */
export function sanitizeTranslations(raw) {
    const out = {};
    if (!raw || typeof raw !== 'object')
        return out;
    const r = raw;
    for (const lang of EXTRA_TRANSLATION_LANGUAGES) {
        const t = sanitizeOne(r[lang], lang);
        if (t)
            out[lang] = t;
    }
    return out;
}
/** Later sources win per language (e.g. a fresh server answer over what a card already had). */
export function mergeTranslations(...sources) {
    const out = {};
    for (const src of sources) {
        if (!src)
            continue;
        for (const lang of EXTRA_TRANSLATION_LANGUAGES) {
            const t = src[lang];
            if (!t?.translation)
                continue;
            // A newer translation without an example keeps the example the card already had.
            out[lang] = t.example ? t : { ...out[lang], translation: t.translation };
        }
    }
    return out;
}
/** Extra languages a card has no translation (or no translated example) for yet. */
export function missingTranslationLanguages(card) {
    return EXTRA_TRANSLATION_LANGUAGES.filter((lang) => {
        const t = card.translations?.[lang];
        return !t?.translation || (!!card.example_target && !t.example);
    });
}
/** What a card shows for `lang`: that language where available, otherwise the English original. */
export function translationFor(card, lang) {
    const t = lang === 'en' ? undefined : card.translations?.[lang];
    return {
        text: t?.translation ?? card.back_text ?? '',
        textLang: t?.translation ? lang : 'en',
        example: t?.example ?? card.example_native,
        exampleLang: t?.example ? lang : 'en',
    };
}
